import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminGroupsAPI,
  type AdminCreateGroupData,
} from "@/services/adminGroupsAPI";

// Query keys
export const adminGroupsKeys = {
  all: ["admin", "groups"] as const,
  lists: () => [...adminGroupsKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...adminGroupsKeys.lists(), filters] as const,
  details: () => [...adminGroupsKeys.all, "detail"] as const,
  detail: (id: string) => [...adminGroupsKeys.details(), id] as const,
  stats: () => [...adminGroupsKeys.all, "stats"] as const,
  reported: () => [...adminGroupsKeys.all, "reported"] as const,
};

// Hook for creating a group as admin
export const useAdminCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupData: AdminCreateGroupData) =>
      adminGroupsAPI.createGroup(groupData),
    onSuccess: () => {
      // Invalidate groups list and stats
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.stats() });
    },
    onError: (error: Error) => {
      console.error("Create group error:", error.message);
    },
  });
};

// Hook for uploading group image
export const useAdminUploadGroupImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, file }: { groupId: string; file: File }) =>
      adminGroupsAPI.uploadGroupImage(groupId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminGroupsKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.lists() });
    },
    onError: (error: Error) => {
      console.error("Image upload error:", error.message);
    },
  });
};

// Hook for uploading group video
export const useAdminUploadGroupVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, file }: { groupId: string; file: File }) =>
      adminGroupsAPI.uploadGroupVideo(groupId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminGroupsKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.lists() });
    },
    onError: (error: Error) => {
      console.error("Video upload error:", error.message);
    },
  });
};

// Hook for getting all groups
export const useAdminGroups = (params?: {
  page?: number;
  limit?: number;
  status?: "Active" | "Expired";
  activityType?: string;
  search?: string;
}) => {
  return useQuery({
    queryKey: adminGroupsKeys.list(params || {}),
    queryFn: () => adminGroupsAPI.getAllGroups(params),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

// Hook for getting a single group
export const useAdminGroup = (groupId: string) => {
  return useQuery({
    queryKey: adminGroupsKeys.detail(groupId),
    queryFn: () => adminGroupsAPI.getGroupById(groupId),
    enabled: !!groupId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
};

// Hook for updating a group
export const useAdminUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      updateData,
    }: {
      groupId: string;
      updateData: Partial<AdminCreateGroupData>;
    }) => adminGroupsAPI.updateGroup(groupId, updateData),
    onSuccess: (_, variables) => {
      // Invalidate the specific group and groups list
      queryClient.invalidateQueries({
        queryKey: adminGroupsKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.stats() });
    },
    onError: (error: Error) => {
      console.error("Update group error:", error.message);
    },
  });
};

// Hook for deleting a group
export const useAdminDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => adminGroupsAPI.deleteGroup(groupId),
    onSuccess: () => {
      // Invalidate groups list and stats
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.stats() });
    },
    onError: (error: Error) => {
      console.error("Delete group error:", error.message);
    },
  });
};

// Hook for getting group statistics
export const useAdminGroupStats = () => {
  return useQuery({
    queryKey: adminGroupsKeys.stats(),
    queryFn: () => adminGroupsAPI.getGroupStats(),
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
  });
};

// Hook for getting reported groups
export const useAdminReportedGroups = (params?: {
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: [...adminGroupsKeys.reported(), params || {}],
    queryFn: () => adminGroupsAPI.getReportedGroups(params),
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
};

// Hook for suspending/unsuspending a group
export const useAdminToggleGroupStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, suspend }: { groupId: string; suspend: boolean }) =>
      adminGroupsAPI.toggleGroupStatus(groupId, suspend),
    onSuccess: (_, variables) => {
      // Invalidate the specific group and groups list
      queryClient.invalidateQueries({
        queryKey: adminGroupsKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.stats() });
      queryClient.invalidateQueries({ queryKey: adminGroupsKeys.reported() });
    },
    onError: (error: Error) => {
      console.error("Toggle group status error:", error.message);
    },
  });
};
